// src/pages/POS/posSlice.js
import { createSlice } from "@reduxjs/toolkit";
import Big from "big.js";
import {
  fetchPosVoucherNumber,
  fetchCustomers,
  fetchCustomerSpecialPrice,
  fetchItems,
  fetchSalesAccounts,
  createSales,
  fetchItemStock,
  fetchTaxAccounts,
  fetchDiscountAccounts,
  fetchTransportAccounts
} from "./posThunk";
import { currentDateTime } from "../../lib/functions";

const initialState = {
  voucherNumber: "",
  entryDate: currentDateTime(),
  customer: null,
  customers: [],
  items: [],
  stockInfo: {},
  salesAccounts: [],
  selectedSalesAcc: null,
  selectedTaxAcc: null,
  selectedDiscountAcc : null,
  selectedTransportAcc: null,

  itemCart: [],
  emis: [],
  specialPrice: null,
  cartMode: "auto", // ✅ added cart mode
  taxMode: "on_item",
  payCart: [],
  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    description: "",
    discountPct: 0,
    discountAmt: 0,
    taxPct: 0,
    paidAmount: 0,
    dueAmount: 0,
    payCart: [],
    payServiceCart: [],
    emis: [],
    serviceCharge: 0,
    addExpenses: false,
    isEMI: false,
  },
  productImage: null,
  loading: false,
  error: null,
};

const posSlice = createSlice({
  name: "pos",
  initialState,
  reducers: {
    resetPos: () => ({
      ...initialState,
      footerValues: {
        ...initialState.footerValues,
        payCart: [],
        payServiceCart: [],
        emis: [],
      },
    }),
    addItemRow: (state, { payload }) => {
      state.itemCart.push(payload);
    },
    updateItemRow: (state, { payload }) => {
      const { key, updates } = payload;
      const index = state.itemCart.findIndex((r) => r.key === key);
      if (index !== -1) {
        state.itemCart[index] = { ...state.itemCart[index], ...updates };
      }
    },
    deleteItemRow: (state, { payload }) => {
      state.itemCart = state.itemCart.filter((r) => r.key !== payload);
    },
    setCustomer: (state, { payload }) => {
      state.customer = payload;
    },
    setPreviousDue: (state, { payload }) => {
      state.previousDue = payload;
    },
    setProductImage: (state, { payload }) => {
      state.productImage = payload;
    },
    setFooterValues: (state, { payload }) => {
    state.footerValues = { ...state.footerValues, ...payload };
    },
    setCartMode: (state, { payload }) => {
      state.cartMode = payload; // ✅ manual vs auto
    },
    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },
    setPayCart: (state, { payload }) => {
      state.footerValues.payCart = payload || [];
      state.payCart = payload || [];
    },
    // calculateTotals: (state, authInfo) => {
    //   const subTotal = state.itemCart.reduce(
    //     (sum, r) => sum + (parseFloat(r.item_total) || (r.qty || 0) * (r.rate || 0)),
    //     0
    //   );
    //   const totalAmount =
    //     subTotal +
    //     (state.footerValues.totalTax || 0) -
    //     (state.footerValues.totalDiscount || 0);

    //   state.footerValues.subTotal = +subTotal.toFixed(2);
    //   state.footerValues.totalAmount = +totalAmount.toFixed(2);
    //   state.footerValues.dueAmount = +(
    //     totalAmount - (state.footerValues.paidAmount || 0)
    //   ).toFixed(2);
    // },
    calculateTotals(state, action) {
  const authInfo = action?.payload?.authInfo || {};
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;

  let subtotal = new Big(0);
  let bareSubtotal = new Big(0);
  let totalDiscount = new Big(0);
  let totalTax = new Big(0);

  state.itemCart.forEach((it) => {
    const qty = new Big(it.qty || 0);
    const rate = new Big(it.rate || 0);

    if (it?.bonus) return;

    const line = qty.times(rate);

    if (state.taxMode === "on_item") {
      const discountPct = new Big(it.discount || 0);

      const discountAmt = line.times(discountPct).div(100);
      const afterDiscount = line.minus(discountAmt);

      const taxPer = showTax ? new Big(it.tax_per || 0) : new Big(0);
      const taxAmt = afterDiscount.times(taxPer).div(100);

      const inLine = afterDiscount.plus(taxAmt);

      bareSubtotal = bareSubtotal.plus(line);
      subtotal = subtotal.plus(inLine);
      totalDiscount = totalDiscount.plus(discountAmt);
      totalTax = totalTax.plus(taxAmt);

    } else {
      subtotal = subtotal.plus(line);
    }
  });

  let resolvedDiscountPct = state.footerValues.discountPct;

  if (state.taxMode === "on_total") {
    const footerDiscountMode = state.footerValues.discountMode === "amount" ? "amount" : "percent";
    if (footerDiscountMode === "amount") {
      const raw = new Big(state.footerValues.discountAmt || 0);
      totalDiscount = raw.gt(subtotal) ? subtotal : raw.lt(0) ? new Big(0) : raw;
      // Back-computed so the printed voucher (which reads this field
      // directly) is correct even when the footer is in amount mode.
      resolvedDiscountPct = subtotal.eq(0) ? 0 : Number(totalDiscount.div(subtotal).times(100).toFixed(13));
    } else {
      const discountPct = Number(state.footerValues.discountPct) || 0;
      totalDiscount = subtotal.times(discountPct).div(100);
      resolvedDiscountPct = discountPct;
    }

    const afterDiscount = subtotal.minus(totalDiscount);

    const taxPct = showTax ? new Big(authInfo?.item_tax || 0) : new Big(0);
    totalTax = afterDiscount.times(taxPct).div(100);

    state.footerValues.taxPct = Number(taxPct);
  }

  const serviceCharge = new Big(state.footerValues.serviceCharge || 0);

  const expenseTotal = (state.footerValues.payServiceCart || []).reduce(
    (sum, r) => sum.plus(r.tran_amount || 0),
    new Big(0)
  );

  const paidAmount = new Big(state.footerValues.paidAmount || 0);

  const grandTotal =
    state.taxMode === "on_item"
      ? bareSubtotal
          .minus(totalDiscount)
          .plus(totalTax)
          .plus(serviceCharge)
          .plus(expenseTotal)
      : subtotal
          .minus(totalDiscount)
          .plus(totalTax)
          .plus(serviceCharge)
          .plus(expenseTotal);

  const dueAmount = grandTotal.minus(paidAmount);

  state.footerValues = {
    ...state.footerValues,

    subtotal: subtotal.toString(),
    total_discount: totalDiscount.toString(),
    discountPct: resolvedDiscountPct,
    totalTax: totalTax.toString(),
    grandTotal: grandTotal.toString(),
    dueAmount: dueAmount.toString(),
  };
}
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosVoucherNumber.fulfilled, (state, { payload }) => {
        state.voucherNumber = payload;
      })
      .addCase(fetchCustomers.fulfilled, (state, { payload }) => {
        state.customers = payload;
      })
      .addCase(fetchItems.fulfilled, (state, { payload }) => {
        state.items = payload;
      })
      .addCase(fetchSalesAccounts.fulfilled, (state, { payload }) => {
        state.salesAccounts = payload;
        if (payload.length > 0) {
          state.selectedSalesAcc = payload[0];
        }
      })
      .addCase(fetchTaxAccounts.fulfilled, (state, { payload }) => {
        state.selectedTaxAcc = payload;
      })
      .addCase(fetchDiscountAccounts.fulfilled, (state, { payload }) => {
        state.selectedDiscountAcc = payload;
      })
      .addCase(fetchTransportAccounts.fulfilled, (state, { payload }) => {
        state.selectedTransportAcc = payload;
      })
      .addCase(createSales.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSales.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createSales.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchCustomerSpecialPrice.fulfilled, (state, { payload }) => {
        state.specialPrice = payload?.special_price || null;
      })
      .addCase(fetchItemStock.fulfilled, (state, { payload }) => {
        if (!payload || !payload[0]) return;
        const stock = payload[0];
        state.stockInfo[stock.item_id] = stock.current_qty || 0;
      });
  },
});

export const {
  resetPos,
  addItemRow,
  updateItemRow,
  deleteItemRow,
  setCustomer,
  setFooterValues,
  setCartMode, // ✅ export new reducer
  setPayCart,
  setTaxMode,
  setPreviousDue,
  setProductImage,
  calculateTotals,
} = posSlice.actions;

export default posSlice.reducer;
